import React, { useRef, useState, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Container,
  Grid,
  TextField,
  Button,
  Box,
  Typography,
  Snackbar,
  Alert,
  useTheme,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import InstagramIcon from "@mui/icons-material/Instagram";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import SendIcon from "@mui/icons-material/Send";
import CONTACT_INFO from "../constants/contactInfo";
import Reveal from "../components/Reveal";
import { PageHero, Section, SectionTitle } from "../components/layout";
import emailjs from "@emailjs/browser";

const HERO_IMAGE =
  "https://ik.imagekit.io/cabanasdelcampo/exteriores/DCS_3124_1.jpg?updatedAt=1760707738623";

const CANALES = [
  {
    icon: WhatsAppIcon,
    titulo: "WhatsApp",
    detalle: "Respuesta más rápida",
    href: CONTACT_INFO.reservarLink,
    externo: true,
    destacado: true,
  },
  {
    icon: PhoneOutlinedIcon,
    titulo: "Teléfono",
    detalle: CONTACT_INFO.phone,
    href: `tel:${CONTACT_INFO.phone.replace(/\s/g, "")}`,
  },
  {
    icon: EmailOutlinedIcon,
    titulo: "Email",
    detalle: CONTACT_INFO.email,
    href: `mailto:${CONTACT_INFO.email}`,
  },
  {
    icon: InstagramIcon,
    titulo: "Instagram",
    detalle: CONTACT_INFO.instagram,
    href: CONTACT_INFO.instagramLink,
    externo: true,
  },
];

const Contacto = () => {
  const form = useRef();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: "", message: "" });
  const theme = useTheme();

  useEffect(() => {
    emailjs.init("n24yDXbzYxQncgvYB");
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: "", message: "" });

    emailjs
      .send("service_hu2loj8", "template_dk6qvfx", {
        name: form.current.name.value,
        email: form.current.email.value,
        phone: form.current.phone.value,
        message: form.current.message.value,
      })
      .then(() => {
        setSubmitStatus({
          type: "success",
          message: "¡Mensaje enviado! Te vamos a responder a la brevedad.",
        });
        form.current.reset();
      })
      .catch(() => {
        setSubmitStatus({
          type: "error",
          message: "No pudimos enviar el mensaje. Probá de nuevo o escribinos por WhatsApp.",
        });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <Box>
      <PageHero image={HERO_IMAGE} imagePosition="center 55%" overline="Contacto" title="Hablemos">
        Escribinos y te ayudamos a planear tu estadía en Cabañas del Campo.
      </PageHero>

      {/* Canales de contacto */}
      <Container sx={{ mt: { xs: -4, md: -6 }, position: "relative", zIndex: 1 }}>
        <Grid container spacing={2}>
          {CANALES.map(({ icon: Icon, titulo, detalle, href, externo, destacado }, index) => (
            <Grid item xs={12} sm={6} md={3} key={titulo}>
              <Reveal delay={index * 80} sx={{ height: "100%" }}>
                <Box
                  component="a"
                  href={href}
                  {...(externo ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  sx={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 2.5,
                    borderRadius: "20px",
                    textDecoration: "none",
                    color: destacado ? "white" : "text.primary",
                    backgroundColor: destacado ? theme.palette.secondary.dark : "background.paper",
                    boxShadow: "0 20px 50px -20px rgba(35,31,32,0.25)",
                    transition: "transform .25s ease, box-shadow .25s ease",
                    "&:hover": {
                      transform: "translateY(-3px)",
                      boxShadow: "0 24px 50px -18px rgba(35,31,32,0.35)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      flexShrink: 0,
                      borderRadius: "50%",
                      display: "grid",
                      placeItems: "center",
                      backgroundColor: destacado ? "rgba(255,255,255,0.12)" : "rgba(203,110,40,0.12)",
                      color: destacado ? "primary.light" : "primary.main",
                    }}
                  >
                    <Icon />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600, color: "inherit" }}>{titulo}</Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: destacado ? "rgba(255,255,255,0.8)" : "text.secondary",
                        // Los datos largos (como el email) bajan de línea en vez de cortarse
                        overflowWrap: "anywhere",
                      }}
                    >
                      {detalle}
                    </Typography>
                  </Box>
                </Box>
              </Reveal>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Formulario y ubicación */}
      <Section sx={{ mb: "-20px" }}>
        <Container>
          <Reveal>
            <SectionTitle overline="Dejanos tu mensaje" title="Escribinos">
              Contanos las fechas que tenés en mente y cuántas personas viajan,
              y te respondemos a la brevedad.
            </SectionTitle>
          </Reveal>
          {/* Formulario y mapa empiezan y terminan a la misma altura */}
          <Grid container spacing={{ xs: 6, md: 8 }} alignItems="stretch">
            <Grid item xs={12} md={7}>
              <Reveal>
                <Box
                  component="form"
                  ref={form}
                  onSubmit={handleSubmit}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 2.5,
                  }}
                >
                  <TextField fullWidth label="Nombre" name="name" required autoComplete="name" />
                  <TextField
                    fullWidth
                    label="Teléfono"
                    name="phone"
                    type="tel"
                    autoComplete="tel"
                  />
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    required
                    type="email"
                    autoComplete="email"
                    sx={{ gridColumn: { sm: "1 / -1" } }}
                  />
                  <TextField
                    fullWidth
                    label="Mensaje"
                    name="message"
                    required
                    multiline
                    rows={7}
                    sx={{ gridColumn: { sm: "1 / -1" } }}
                  />
                  {/* El margen inferior compensa el padding de la tarjeta del mapa para alinear los botones */}
                  <Box
                    sx={{
                      gridColumn: { sm: "1 / -1" },
                      display: "flex",
                      justifyContent: "flex-end",
                      mb: { md: "25px" },
                    }}
                  >
                    <Button
                      size="large"
                      type="submit"
                      variant="contained"
                      disabled={isSubmitting}
                      endIcon={<SendIcon sx={{ fontSize: "1rem !important" }} />}
                      sx={{ px: 4, border: "1.5px solid transparent" }}
                    >
                      {isSubmitting ? "Enviando..." : "Enviar mensaje"}
                    </Button>
                  </Box>
                </Box>
              </Reveal>
            </Grid>

            <Grid item xs={12} md={5}>
              <Reveal delay={150} sx={{ height: "100%" }}>
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    p: 3,
                    borderRadius: "24px",
                    backgroundColor: theme.palette.sand.light,
                    border: `1px solid ${theme.palette.sand.dark}`,
                  }}
                >
                  <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", mb: 2 }}>
                    <LocationOnOutlinedIcon sx={{ color: "primary.main", mt: 0.3 }} />
                    <Box>
                      <Typography variant="h6" sx={{ fontSize: "1.1rem" }}>
                        Dónde estamos
                      </Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        {CONTACT_INFO.address}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      position: "relative",
                      flex: 1,
                      minHeight: 200,
                      borderRadius: "16px",
                      overflow: "hidden",
                      mb: 2.5,
                    }}
                  >
                    <iframe
                      src={CONTACT_INFO.googleMapsLink}
                      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: "none" }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Ubicación de Cabañas del Campo en Athos Pampa"
                    />
                  </Box>
                  <Button
                    variant="outlined"
                    component={RouterLink}
                    to="/como-llegar"
                    size="large"
                    sx={{ alignSelf: "flex-end" }}
                  >
                    Cómo llegar
                  </Button>
                </Box>
              </Reveal>
            </Grid>
          </Grid>
        </Container>
      </Section>

      <Snackbar
        open={!!submitStatus.message}
        autoHideDuration={6000}
        onClose={() => setSubmitStatus({ type: "", message: "" })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSubmitStatus({ type: "", message: "" })}
          severity={submitStatus.type || "info"}
          variant="filled"
          sx={{ borderRadius: "12px" }}
        >
          {submitStatus.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Contacto;
